import React from 'react';
import { Checkbox, Modal } from 'antd';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import orthoReadJson from './orthoReadJson';

/**
 * 建模流程动作：就绪/申请/审批/重建/清空航片等
 */
export default function useOrthoModelingActions({
  activeId,
  activeTask,
  setBusy,
  code,
  setCode,
  wantGlb,
  purgeInboxOnSuccess,
  setRevealedCode,
  loadTasks,
  loadPending,
  loadLayers,
  refreshStatusBar,
  photoRenderBoundsRef,
  clearPhotoDensityLayer,
  openPhotoPreview
}) {
  const markReady = () => {
    if (!activeId) return;
    const count = Number(activeTask?.assetCount || 0);
    if (count < 1) {
      message.warning('请先点「上传照片」登记素材，再标记就绪');
      return;
    }
    Modal.confirm({
      title: '确认素材已就绪？',
      content: `当前已登记 ${count} 张。确认后可申请启动建模。若误点，可再点「撤回就绪」。`,
      okText: '确认就绪',
      cancelText: '取消',
      onOk: async () => {
        setBusy(true);
        try {
          await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/ready`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({})
          }));
          message.success('已标记素材就绪（可撤回）');
          await loadTasks();
        } catch (e) {
          message.error(e.message);
          throw e;
        } finally {
          setBusy(false);
        }
      }
    });
  };

  const unreadyTask = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/unready`, { method: 'POST' }));
      message.success(data.message || '已撤回就绪');
      await loadTasks();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const withdrawRequest = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/withdraw`, { method: 'POST' }));
      message.success(data.message || '已撤销申请');
      await loadTasks();
      await loadPending();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  /** 保留航片或换场景清空航片；归档旧图层并清理旧成果，回到 ready */
  const prepareRemodel = async () => {
    if (!activeId) return;
    let purgeAssets = false;
    Modal.confirm({
      title: `准备重建模 · 任务 #${activeId}`,
      width: 520,
      content: (
        <div>
          <p><b>会做：</b>归档旧正射/3D 图层；清理 OSS 上该任务旧瓦片与 GLB；任务回到「就绪」。</p>
          <p><b>默认不做：</b>不删任务；默认保留已登记航片。</p>
          <p style={{ marginTop: 8 }}>
            <Checkbox
              onChange={(e) => { purgeAssets = e.target.checked; }}
            >
              <b>换新场景：同时清空本任务旧航片</b>（建议勾选，否则新旧区域会叠在一起）
            </Checkbox>
          </p>
          <p>完成后请先「密度预览/框选渲染区」，再申请启动。Ubuntu 上请自行删除 <code>/data/ortho-work/task-{activeId}*</code> 工作目录。</p>
        </div>
      ),
      okText: '确认清理并准备重建',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setBusy(true);
        try {
          photoRenderBoundsRef.current = null;
          const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/prepare-remodel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ purgeOutput: true, purgeAssets })
          }));
          message.success(data.message || '已准备重建');
          if (data.ossPurge?.skipped && data.ossPurge?.reason) {
            message.warning(`OSS 清理：${data.ossPurge.reason}`);
          } else if (data.ossPurge?.deleted != null) {
            message.info(`已清理 OSS 对象约 ${data.ossPurge.deleted} 个`);
          }
          if (data.assetPurge && !data.assetPurge.skipped) {
            message.info(`已清空航片登记 ${data.assetPurge.dbDeleted || 0} 条`);
          }
          await loadTasks();
          await loadLayers();
          refreshStatusBar();
          clearPhotoDensityLayer();
          if (purgeAssets) {
            Modal.info({
              title: '下一步：上传新区域航片',
              content: '旧航片已清空。请「上传文件夹」后，再点「密度预览/框选渲染区」。',
              okText: '知道了'
            });
          } else {
            Modal.info({
              title: '下一步：框选渲染区',
              content: '请先「密度预览/框选渲染区」。若地图上出现多个断裂团块，说明仍混有旧场景航片，请点「清空旧航片」后只保留新区照片。',
              okText: '开始密度预览',
              onOk: () => openPhotoPreview()
            });
          }
        } catch (e) {
          message.error(e.message || '准备重建失败');
          throw e;
        } finally {
          setBusy(false);
        }
      }
    });
  };

  /** 换新场景：清空已登记航片（不删任务） */
  const clearTaskAssets = () => {
    if (!activeId) return;
    Modal.confirm({
      title: `清空任务 #${activeId} 旧航片？`,
      content: '将删除本任务已登记的航片记录，并尝试清理 OSS inbox。任务本身保留。清空后请重新上传新区域照片再框选。',
      okText: '确认清空',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setBusy(true);
        try {
          photoRenderBoundsRef.current = null;
          const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/clear-assets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}'
          }));
          message.success(data.message || '已清空航片');
          clearPhotoDensityLayer();
          await loadTasks();
          refreshStatusBar();
        } catch (e) {
          message.error(e.message || '清空失败');
          throw e;
        } finally {
          setBusy(false);
        }
      }
    });
  };

  const abortModeling = async () => {
    if (!activeId) return;
    Modal.confirm({
      title: '中止当前建模？',
      content: '将把任务标为失败，便于重新框选后再开算。假 B 若已在跑请自行停掉。',
      okText: '中止',
      okType: 'danger',
      onOk: async () => {
        setBusy(true);
        try {
          const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/abort`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason: '指挥台中止，待框选后重开' })
          }));
          message.success(data.message || '已中止');
          await loadTasks();
          refreshStatusBar();
        } catch (e) {
          message.error(e.message || '中止失败');
          throw e;
        } finally {
          setBusy(false);
        }
      }
    });
  };

  const requestStart = async (force = false) => {
    if (!activeId) return;
    // 前端先拦一层：未确认选区不要进审批
    if (!force && !activeTask?.renderAoi?.confirmedAt) {
      Modal.confirm({
        title: '尚未框选渲染区',
        content: '重建模前必须先「密度预览/框选渲染区」。未框选开算会再次混入对面山照片。',
        okText: '去框选',
        cancelText: '取消',
        onOk: () => openPhotoPreview()
      });
      return;
    }
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/request-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wantGlb, purgeInboxOnSuccess, force: !!force })
      }));
      message.success(data.message || '已提交申请');
      if (data.task?.approvalCode) setRevealedCode(data.task.approvalCode);
      await loadTasks();
      await loadPending();
      refreshStatusBar();
    } catch (e) {
      if (['PHOTO_DISCONTINUITY', 'PHOTO_AOI_REQUIRED', 'PHOTO_GPS_MISSING'].includes(e.code)) {
        Modal.confirm({
          title: e.code === 'PHOTO_GPS_MISSING' ? '缺少航片 GPS' : '请先框选渲染区',
          width: 520,
          content: (
            <div>
              <p>{e.message}</p>
              <p>请先完成密度预览与框选，再申请启动。</p>
            </div>
          ),
          okText: '去预览并框选',
          cancelText: '取消',
          onOk: () => openPhotoPreview()
        });
      } else {
        message.error(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    if (!activeId || !code.trim()) return;
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${activeId}/confirm-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), wantGlb, purgeInboxOnSuccess })
      }));
      message.success(data.message || '已放行启动');
      setCode('');
      await loadTasks();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const approveTask = async (id) => {
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${id}/approve`, { method: 'POST' }));
      message.success(data.message || '已确认启动');
      // 手动假 B：弹窗展示 bootToken，便于复制到 Ubuntu VM
      if (data.manual && data.bootToken) {
        const base = data.callbackBase || 'http://A主机:5000';
        Modal.info({
          title: '手动假 B · bootToken（请复制到 Ubuntu）',
          width: 560,
          content: (
            <div style={{ wordBreak: 'break-all' }}>
              <div style={{ marginBottom: 8 }}>{data.message}</div>
              <div><b>taskId：</b>{id}</div>
              <div><b>callbackBase：</b>{base}</div>
              <div style={{ marginTop: 8 }}><b>bootToken：</b></div>
              <code style={{ display: 'block', marginTop: 4, padding: 8, background: '#f5f5f5' }}>
                {data.bootToken}
              </code>
              <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                下一步在 VM 用该 token 请求 /api/ortho/tasks/{id}/config
              </div>
            </div>
          )
        });
      }
      await loadTasks();
      await loadPending();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revealCode = async (id) => {
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${id}/reveal-code`, { method: 'POST' }));
      setRevealedCode(data.approvalCode || '');
      message.info(data.message || `验证码 ${data.approvalCode}`);
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rejectTask = async (id) => {
    setBusy(true);
    try {
      await orthoReadJson(await apiFetch(`/api/ortho/tasks/${id}/reject`, { method: 'POST' }));
      message.success('已驳回');
      await loadPending();
      await loadTasks();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return {
    markReady,
    unreadyTask,
    withdrawRequest,
    prepareRemodel,
    clearTaskAssets,
    abortModeling,
    requestStart,
    confirmCode,
    approveTask,
    revealCode,
    rejectTask
  };
}
